import { useState } from 'react';
import { useFirestore } from 'reactfire';
import { useParams } from 'react-router-dom';
import {
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  setDoc,
  query,
  collection,
  getDocs,
} from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useGoogleLogin } from '@react-oauth/google';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

import {
  gridClasses,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';
import { Box, Button } from '@mui/material';
import { ChangeRequestGrid } from '../../../styled/ChangeRequest.styled';
import createChangeRequestsColumns from './ChangeRequestsTableColumns';
import { initialMessages, initialSuggestions } from '../Chat';

import { ChangeRequest } from '../../../types';
import {
  deleteGoogleFile,
  deleteGoogleFolder,
  uploadFileToGoogleDrive,
} from '../../../utils/api/storage';
import { getFormattedDate } from '../../../utils/dateFormat';

interface ChangeRequestsTableProps {
  changeRequests: ChangeRequest[];
  isAdmin: boolean;
}

interface localChangeRequest extends ChangeRequest {
  isNew?: boolean;
}

const ChangeRequestsTable = ({
  changeRequests,
  isAdmin,
}: ChangeRequestsTableProps) => {
  const firestore = useFirestore();
  const { enqueueSnackbar } = useSnackbar();
  const { userUid } = useParams();

  const [requests, setRequests] = useState<localChangeRequest[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [tableLoading, setTableLoading] = useState(false);
  const [googleAuthToken, setGoogleAuthToken] = useState<string | null>(
    Cookies.get('authToken') || null,
  );

  const googleAuth = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      const token = tokenResponse.access_token;
      setGoogleAuthToken(token);
      Cookies.set('authToken', token, { expires: 1 / 24 });
    },
    onError: (error) => console.error('Login Failed:', error),
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleAddNewRequest = () => {
    if (!userUid) return;
    const reqId = uuidv4();
    const newRequest: Partial<ChangeRequest> & {
      id: string;
      userId: string;
      createdAt: Timestamp;
      isNew?: boolean;
    } = {
      id: reqId,
      userId: userUid,
      status: 'pending',
      docs: [],
      createdAt: Timestamp.now(),
      isNew: true,
    };
    setRequests([newRequest as ChangeRequest, ...changeRequests]);

    setRowModesModel((prev) => ({
      ...prev,
      [reqId]: { mode: GridRowModes.Edit },
    }));
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRequests([]);
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleDeleteClick = async (id: GridRowId, createdAt: Timestamp) => {
    if (!userUid) return;
    try {
      setTableLoading(true);
      const changeRequestRef = doc(firestore, 'changeRequests', String(id));
      const changeRequestDocSnap = await getDoc(changeRequestRef);
      const date = getFormattedDate(createdAt);
      const childFolderName = String(id) + ':' + date;

      if (!changeRequestDocSnap.exists()) throw new Error('Document not found');

      const data = changeRequestDocSnap.data();

      if (data['docs'].length) {
        if (!googleAuthToken) {
          googleAuth();
          return;
        } else {
          await deleteGoogleFolder(googleAuthToken, childFolderName);
        }
      }
      await deleteDoc(changeRequestRef);

      const conversationRef = doc(
        firestore,
        `chats/${userUid}/conversations/${id}`,
      );
      const docSnap = await getDoc(conversationRef);
      if (docSnap.exists()) {
        await deleteDoc(conversationRef);
      }
    } catch (error) {
      enqueueSnackbar('Сталася помилка! Спробуйте будь ласка пізніше.', {
        variant: 'error',
      });
    } finally {
      setTableLoading(false);
    }
  };

  const updateDocs = async (
    folderName: string,
    docs: (string | File)[],
    oldDocs: string[],
  ) => {
    const newDocs = docs.filter(
      (doc: string | File) => doc instanceof File,
    ) as File[];

    const existingDocs = docs.filter(
      (doc: string | File) => typeof doc === 'string',
    ) as string[];

    const removedDocs = oldDocs.filter((url) => !existingDocs.includes(url));

    if (!newDocs.length && !removedDocs.length) {
      return existingDocs;
    }

    if (!googleAuthToken) {
      googleAuth();
      return;
    }

    const newDocUrls = await uploadFileToGoogleDrive(
      googleAuthToken,
      newDocs,
      folderName,
    );

    await Promise.allSettled(
      removedDocs.map(async (url) => {
        await deleteGoogleFile(googleAuthToken, url);
      }),
    );

    const allDocUrls = [...existingDocs, ...newDocUrls].filter(
      (url) => !removedDocs.includes(url),
    );

    return allDocUrls;
  };

  const processRowUpdate = async (
    newChangeRequest: GridRowModel,
    oldChangeRequest: GridRowModel,
  ) => {
    const {
      id,
      recordId,
      requestType,
      status,
      rowsCount,
      paymentAmount,
      paymentDate,
      docs,
      comment,
      userId,
      createdAt,
      isNew,
    } = newChangeRequest;
    const changeRequestRef = doc(firestore, 'changeRequests', id);
    const oldDocs: string[] = oldChangeRequest.docs;
    const oldCreatedAt = oldChangeRequest.createdAt;

    try {
      setTableLoading(true);
      const oldDate = getFormattedDate(oldCreatedAt);
      const newDate = getFormattedDate(createdAt);
      const oldChildFolderName = String(id) + ':' + oldDate;
      const newChildFolderName = String(id) + ':' + newDate;

      const updatedDocs = await updateDocs(newChildFolderName, docs, oldDocs);

      if (!updatedDocs) {
        throw new Error('Update delayed by authorization');
      }

      const baseData = {
        recordId,
        createdAt,
        requestType: requestType ?? null,
        status,
        rowsCount: rowsCount ?? null,
        paymentAmount: paymentAmount ?? null,
        paymentDate: paymentDate ?? null,
        docs: updatedDocs,
        comment: comment ?? '',
      };

      const newRequest = {
        ...newChangeRequest,
        docs: updatedDocs,
      };

      if (isNew) {
        await setDoc(changeRequestRef, {
          ...baseData,
          userId,
        });

        //create empty chat
        const conversationRef = doc(
          firestore,
          `chats/${userId}/conversations/${id}`,
        );
        await setDoc(conversationRef, {
          messages: initialMessages,
          suggestions: initialSuggestions,
          code: null,
          createdAt,
          recordId: recordId,
          requestDate: createdAt,
        });

        setRequests([]);
        return newRequest;
      }

      await updateDoc(changeRequestRef, baseData);

      const conversationsCollection = collection(
        firestore,
        `chats/${userUid}/conversations`,
      );
      const conversationsQuery = query(conversationsCollection);
      const querySnapshot = await getDocs(conversationsQuery);

      const hasSession = querySnapshot.docs.some((doc) => doc.id === id);
      if (hasSession) {
        const conversationRef = doc(
          firestore,
          `chats/${userUid}/conversations/${id}`,
        );

        await updateDoc(conversationRef, { recordId });
      }

      return newRequest;
    } catch (error) {
      throw error;
    } finally {
      setTableLoading(false);
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = createChangeRequestsColumns({
    rowModesModel,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleDeleteClick,
    isAdmin,
  });

  return (
    <ChangeRequestGrid
      columns={columns}
      rows={requests.length ? requests : changeRequests}
      editMode="row"
      getRowId={(row) => row.id}
      getRowHeight={() => 'auto'}
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      onRowEditStop={handleRowEditStop}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={(error) => {
        if (error.message.includes('Update delayed by authorization')) {
          console.warn(error.message);
          return;
        }
        enqueueSnackbar('Сталася помилка! Спробуйте будь ласка пізніше.', {
          variant: 'error',
        });
      }}
      loading={tableLoading}
      onCellDoubleClick={(_, event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
      sx={{
        [`& .${gridClasses.cell}`]: {
          py: 1,
        },
      }}
      slots={{
        toolbar: () =>
          isAdmin ? (
            <Box margin={1}>
              <Button onClick={handleAddNewRequest}>Add request</Button>
            </Box>
          ) : null,
      }}
      disableRowSelectionOnClick
    />
  );
};

export default ChangeRequestsTable;
